import classNames from 'classnames'
import { MultipageNavigationBar } from 'front-end/src/pages/topic-detail-page/components/MultipageNavigationBar'
import React, { useState } from 'react'
import { Link, RouteComponentProps } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Trans } from '@lingui/macro'

import { HeadTitle } from 'front-end/src/components/common/head/HeadTitle'
import { TopicVersionType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { staticLinks } from 'shared/linkBuilder'
import IconBan from '../../components/common/icon/IconBan'
import Loader from '../../components/common/loader/Loader'
import { ScormPage } from '../scorm-page/ScormPage'
import TopicVersionTitleAndSummary from '../topic-detail-page/TopicVersionTitleAndSummary'
import { IElement } from '../topic-detail-page/types/IElement'
import { ViewElement } from '../topic-detail-page/ViewElement'
import { VideoAssessmentPage } from '../video-assessment-page/VideoAssessmentPage'
import { useFetchTopicEnrolmentQuery } from './TopicEnrolmentDetailPage.codegen'
import { videoAssessmentTopicVersionTypesForFE } from '../topic-detail-page/StudentTopicDetail'

export enum TopicPageMode {
  PathwayView = 'PathwayView',
  WorkspaceView = 'WorkspaceView',
  Preview = 'Preview',
  Gradebook = 'Gradebook'
}

interface IRouteParams {
  enrolmentId: string
}

export const TopicEnrolmentDetailPage = ({
  match
}: RouteComponentProps<IRouteParams> & { mode: TopicPageMode }) => {
  const { data } = useFetchTopicEnrolmentQuery({
    variables: {
      id: parseInt(match.params.enrolmentId)
    }
  })

  const [currentElementIndex, setCurrentElementIndex] = useState(-1)
  const [previousElementIndex, setPreviousElementIndex] = useState(-1)

  if (!data) {
    return <Loader />
  }

  const topicEnrolment = data.me?.topicEnrolment
  const topicVersion = topicEnrolment?.topicVersion

  if (!topicEnrolment || !topicVersion) {
    return (
      <>
        <div className="Auth__noAccess">
          <IconBan />
          <h2>
            <Trans>You dont have access to this resource enrolment</Trans>
          </h2>
          <p>
            <Trans>
              Go back <Link to={staticLinks.home}>home</Link>
            </Trans>
          </p>
        </div>
      </>
    )
  }

  const isSinglePage = topicVersion.topic_type === TopicVersionType.s
  const isMultiPage = topicVersion.topic_type === TopicVersionType.m
  const isScorm = topicVersion.topic_type === TopicVersionType.scorm

  const setCurrentElement = (newElementIndex: number) => {
    setPreviousElementIndex(currentElementIndex)
    setCurrentElementIndex(newElementIndex)
  }

  const renderElementForSinglepage = (element: IElement) => {
    const { type, id } = element

    return (
      <div className={`page page-${type}`} key={id}>
        <div
          className={`ql-editor page-content page-content-${type} element element-${type} element-mode-view`}
        >
          <ViewElement
            {...element}
            topicId={topicVersion.topic.id}
            quizResponses={topicEnrolment.quizResponses}
            allowRetry={topicVersion?.allow_retry}
          />
        </div>
      </div>
    )
  }

  const renderElementForMultipage = (elements: IElement[]) => {
    const goingRight = previousElementIndex < currentElementIndex

    const transition = {
      initial: { x: goingRight ? 500 : -500, opacity: 0 },
      animate: { x: 0, opacity: 1 },
      exit: { opacity: 0 },
      transition: {
        x: { type: 'tween', easy: 'easeOut', duration: 0.5 },
        opacity: { duration: 0.3 }
      }
    }

    const index = currentElementIndex
    let page = null as any

    if (index === -1) {
      page = (
        <TopicVersionTitleAndSummary
          key={index}
          title={topicVersion.title}
          summary={topicVersion.summary}
        />
      )
    }

    const element = elements[index]

    if (element) {
      const { type } = element

      page = (
        <motion.div key={index} className={`page page-${type}`} {...transition}>
          <div
            className={`ql-editor page-content page-content-${type} element element-${type} element-mode-view`}
          >
            <ViewElement
              {...element}
              topicId={topicVersion.topic.id}
              quizResponses={topicEnrolment?.quizResponses}
              allowRetry={!!topicVersion?.allow_retry}
            />
          </div>
        </motion.div>
      )
    }

    return <AnimatePresence>{page}</AnimatePresence>
  }

  const canGoToNextElement = () => {
    const elements = topicVersion.publishedElements || []

    return currentElementIndex <= elements.length - 2
  }

  const progress = () => {
    if (!topicVersion || !topicVersion.publishedElements?.length) {
      return 0
    }

    const singleElemProgressSize = 100 / topicVersion.publishedElements.length
    return singleElemProgressSize * (currentElementIndex + 1)
  }

  const renderElementsOrCurrentElement = () => {
    const elements = topicVersion.publishedElements

    if (topicVersion.topic_type === TopicVersionType.scorm) {
      return <ScormPage shareId={topicEnrolment.id} isPathway={false} />
    }

    if (
      videoAssessmentTopicVersionTypesForFE.includes(topicVersion.topic_type)
    ) {
      return (
        <VideoAssessmentPage shareId={topicEnrolment.id} isPathway={false} />
      )
    } else if (elements) {
      if (topicVersion.topic_type === TopicVersionType.m) {
        return renderElementForMultipage(elements as any as IElement[])
      } else {
        return elements.map((element) => {
          return renderElementForSinglepage(element as any as IElement)
        })
      }
    }
  }

  return (
    <div
      className={classNames('panel topic-mode-learn', {
        'topic-view-single': isSinglePage || isScorm,
        'topic-view-multi': isMultiPage
      })}
    >
      <HeadTitle title={topicVersion.title} />
      {/* FYI: Is not yet implemented, because designs are not yet confirmed. */}
      {/* <TopbarView
        topic={topic}
        onExit={() => history.goBack()}
        mode={this.props.mode}
      ></TopbarView> */}

      {isMultiPage && (
        <div className="topic-progress">
          <div
            className="topic-progress-current"
            style={{ width: `${progress()}%` }}
          />
        </div>
      )}

      <div className="panel-content">
        <div
          className={classNames('pages-wrapper', {
            'full-wrapper': !isSinglePage || !isMultiPage
          })}
        >
          {isSinglePage && (
            <div className="page page-system-generated-title">
              <TopicVersionTitleAndSummary
                title={topicVersion.title}
                summary={topicVersion.summary}
              />
            </div>
          )}
          {renderElementsOrCurrentElement()}
        </div>
      </div>
      {isMultiPage && (
        <MultipageNavigationBar
          canGoToNextElement={canGoToNextElement}
          currentElementIndex={currentElementIndex}
          setCurrentElement={setCurrentElement}
        />
      )}
    </div>
  )
}
